import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { sa, SaButton, Field, errMsg } from './ui';
import { apiOrigin } from '../../lib/site';

// Field schema per section: [path (relative to section), label, type, options?]
export const SECTION_META = {
  profile: { label: 'Company Profile', fields: [
    ['legal_name', 'Legal / business name'], ['dba', 'Display name (DBA)'], ['tax_id', 'EIN / Tax ID'], ['email', 'Business email'],
    ['phone', 'Phone'], ['website', 'Website'], ['address.street', 'Street'], ['address.city', 'City'], ['address.state', 'State'],
    ['address.zip', 'ZIP'], ['address.country', 'Country'] ] },
  branding: { label: 'Branding & Storefront', fields: [
    ['primary_color', 'Primary color', 'color'], ['secondary_color', 'Secondary color', 'color'], ['accent_color', 'Accent color', 'color'],
    ['storefront_headline', 'Storefront headline'], ['storefront_tagline', 'Storefront tagline'], ['storefront_about', 'About text', 'textarea'] ] },
  domains: { label: 'Storefront Domains', fields: [
    ['custom_domain', 'Custom domain (e.g. yourshop.com) — becomes the primary customer-facing address'],
    ['subdomain', 'Platform subdomain label (defaults to the company slug)'],
    ['redirect_subdomain_to_custom', 'Redirect the platform subdomain to the custom domain', 'bool'],
    ['primary_url', 'Legacy URL override (leave blank to use the domains above)'] ],
    note: 'Customer links (proposals, approvals, payments, tracking, portal) use the custom domain when set, otherwise {slug}.platform-domain. To connect a custom domain, point a CNAME at the platform and TLS is provisioned on the hosting side.' },
  email: { label: 'Email Sender', fields: [
    ['from_name', 'From name'], ['from_email', 'From email'], ['reply_to', 'Reply-to'], ['host', 'SMTP host'], ['port', 'SMTP port', 'number'],
    ['username', 'SMTP username'], ['password', 'SMTP password / app password', 'secret'], ['internal_alert_emails', 'Internal alert emails (comma separated)'],
    ['sales_notify_email', 'Sales notification email'], ['signature', 'Email signature', 'textarea'], ['paused', 'Pause all outgoing emails', 'bool'] ] },
  pricing: { label: 'Pricing & Markup', fields: [
    ['garment_markup', 'Garment markup multiplier (× actual wholesale, default 2.1)', 'number'],
    ['default_markup_pct', 'Default markup %', 'number'], ['min_order_qty', 'Minimum order quantity', 'number'], ['min_order_amount', 'Minimum order amount ($)', 'number'],
    ['rush_fee_pct', 'Rush fee %', 'number'], ['quote_validity_days', 'Quote validity (days)', 'number'] ],
    note: 'Garment sell price = actual per-size wholesale (S&S / SanMar) × the multiplier above; 2XL+ use real size costs, never a second surcharge. Screen-print, embroidery, DTF and sublimation matrices plus screen/setup charges are edited on the Price Matrices page (per company).' },
  tax: { label: 'Tax', fields: [['default_rate', 'Default sales tax rate (e.g. 0.07 = 7%)', 'number'], ['tax_shipping', 'Tax shipping', 'bool'], ['tax_setup_fees', 'Tax setup fees', 'bool']] },
  shipping: { label: 'Shipping', fields: [
    ['shippo_api_key', 'Shippo API key', 'secret'], ['passthrough_label_cost', 'Add label cost to customer balance', 'bool'], ['default_carrier', 'Default carrier'],
    ['flat_rate', 'Flat shipping rate ($, 0 = none)', 'number'], ['origin.name', 'Ship-from name'], ['origin.street1', 'Ship-from street'], ['origin.city', 'Ship-from city'],
    ['origin.state', 'Ship-from state'], ['origin.zip', 'Ship-from ZIP'], ['origin.phone', 'Ship-from phone'] ] },
  suppliers: { label: 'Suppliers / APIs', fields: [
    ['sanmar.enabled', 'SanMar enabled', 'bool'], ['sanmar.customer_number', 'SanMar customer number'], ['sanmar.username', 'SanMar username'],
    ['sanmar.password', 'SanMar password', 'secret'], ['sanmar.ftp_host', 'SanMar FTP host'], ['sanmar.ftp_port', 'SanMar FTP port', 'number'],
    ['ss_activewear.enabled', 'S&S Activewear enabled', 'bool'], ['ss_activewear.account_number', 'S&S account number'], ['ss_activewear.api_key', 'S&S API key', 'secret'] ] },
  payments: { label: 'Payment Processor', fields: [
    ['provider', 'Provider', 'select', ['none', 'inspyre', 'stripe']], ['inspyre.security_key', 'Inspyre/NMI security key', 'secret'],
    ['inspyre.tokenization_key', 'Inspyre/NMI tokenization key', 'secret'], ['inspyre.merchant_url', 'Merchant descriptor URL'], ['inspyre.industry', 'Industry code'],
    ['inspyre.accepted_brands', 'Accepted card brands (comma separated)'], ['stripe.api_key', 'Stripe secret key', 'secret'], ['stripe.webhook_secret', 'Stripe webhook secret', 'secret'] ] },
  documents: { label: 'Proposal / Invoice Branding & Terms', fields: [
    ['proposal_footer', 'Proposal PDF footer', 'textarea'], ['invoice_notes', 'Invoice notes', 'textarea'], ['terms_and_conditions', 'Terms & conditions', 'textarea'],
    ['show_logo_on_pdf', 'Show logo on PDFs', 'bool'] ] },
  features: { label: 'Features', fields: [
    ['customer_portal', 'Customer portal', 'bool'], ['public_catalog', 'Public catalog', 'bool'], ['ai_assistant', 'AI assistant & sales chat', 'bool'],
    ['design_studio', 'Design studio', 'bool'], ['shipping_labels', 'Shipping labels', 'bool'], ['online_payments', 'Online payments', 'bool'],
    ['followup_emails', 'Automated follow-up emails', 'bool'], ['proof_of_pickup', 'Proof of pickup', 'bool'], ['leads_inbox', 'Leads inbox', 'bool'],
    ['past_work_gallery', 'Past work gallery', 'bool'], ['marketing_site', 'Marketing storefront page', 'bool'] ] },
  ai: { label: 'AI Sales Agent', fields: [['assistant_name', 'Assistant name'], ['sales_facts', 'Key facts the agent may quote (one per line, start with -)', 'textarea']] },
  integrations: { label: 'Integrations — Google Sheets lead sync', fields: [
    ['google_sheets.enabled', 'Sync new quote requests / leads to Google Sheets', 'bool'],
    ['google_sheets.web_app_url', 'Apps Script Web App URL (https://script.google.com/macros/s/…/exec)'],
    ['google_sheets.worksheet', 'Worksheet tab name (created if missing)'],
    ['google_sheets.shared_secret', 'Shared secret (normally created with "Generate secret" in the panel below)', 'secret'] ],
    note: 'No Google Cloud project needed: the sheet runs a small Apps Script web app. The secret is encrypted at rest, never shown again and never sent in URLs — requests are HMAC-signed. Use the Google Sheets panel below to generate the secret, copy the script, test, backfill and retry.' },
  permissions: { label: 'Company Admin Permissions', fields: [] },
};

const getP = (o, p) => p.split('.').reduce((a, k) => (a == null ? a : a[k]), o);
const setP = (o, p, v) => { const ks = p.split('.'); let c = o; ks.slice(0, -1).forEach((k) => { c[k] = c[k] || {}; c = c[k]; }); c[ks[ks.length - 1]] = v; return o; };

export const SettingsEditor = ({ baseUrl, logoUrl, sections, initial, onSaved, companyId }) => {
  const [data, setData] = useState(initial || {});
  const [draft, setDraft] = useState({});
  const [active, setActive] = useState(sections[0]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  useEffect(() => { setData(initial || {}); setDraft({}); }, [initial]);

  const value = (sec, path) => { const d = getP(draft, `${sec}.${path}`); return d !== undefined ? d : getP(data, `${sec}.${path}`); };
  const change = (sec, path, v) => setDraft((d) => setP({ ...d }, `${sec}.${path}`, v));

  const save = async () => {
    if (!Object.keys(draft).length) return toast.info('Nothing to save');
    setSaving(true);
    try {
      const { data: res } = await axios.patch(baseUrl, draft);
      setData(res.settings); setDraft({});
      toast.success('Settings saved');
      onSaved?.(res);
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data: res } = await axios.post(logoUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setData(res.settings); toast.success('Logo uploaded'); onSaved?.(res);
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); }
  };

  const meta = SECTION_META[active] || { label: active, fields: [] };
  const renderField = ([path, label, type = 'text', options]) => {
    const v = value(active, path);
    const id = `setting-${active}-${path.replace(/\./g, '-')}`;
    if (type === 'secret') {
      const configured = v && typeof v === 'object' ? v.configured : Boolean(v);
      const hint = v && typeof v === 'object' ? v.hint : '';
      return (
        <Field key={path} label={`${label} ${configured ? `· configured ${hint}` : '· not set'}`}>
          <input data-testid={id} type="password" style={sa.input} placeholder={configured ? 'Enter a new value to replace' : 'Enter value'} autoComplete="new-password"
            value={typeof v === 'string' ? v : ''} onChange={(e) => change(active, path, e.target.value)} />
        </Field>);
    }
    if (type === 'bool') {
      return (
        <label key={path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', fontSize: 14, cursor: 'pointer' }}>
          <input data-testid={id} type="checkbox" checked={Boolean(v)} onChange={(e) => change(active, path, e.target.checked)} /> {label}
        </label>);
    }
    if (type === 'select') {
      return (<Field key={path} label={label}><select data-testid={id} style={sa.input} value={v ?? ''} onChange={(e) => change(active, path, e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}</select></Field>);
    }
    if (type === 'textarea') {
      return (<Field key={path} label={label}><textarea data-testid={id} style={{ ...sa.input, minHeight: 90 }} value={v ?? ''} onChange={(e) => change(active, path, e.target.value)} /></Field>);
    }
    if (type === 'color') {
      return (<Field key={path} label={label}><div style={{ display: 'flex', gap: 8 }}>
        <input type="color" value={v || '#000000'} onChange={(e) => change(active, path, e.target.value)} style={{ width: 44, height: 40, border: '1px solid #27272A', background: 'none' }} />
        <input data-testid={id} style={sa.input} value={v ?? ''} onChange={(e) => change(active, path, e.target.value)} /></div></Field>);
    }
    return (<Field key={path} label={label}><input data-testid={id} type={type} style={sa.input} value={v ?? ''} onChange={(e) => change(active, path, e.target.value)} /></Field>);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, minHeight: 420 }} data-testid="settings-editor">
      <nav style={{ borderRight: '1px solid #27272A', paddingRight: 8 }}>
        {sections.map((s) => (
          <button key={s} type="button" data-testid={`settings-tab-${s}`} onClick={() => setActive(s)} style={{
            display: 'block', width: '100%', textAlign: 'left', background: active === s ? '#1f1f22' : 'transparent', color: active === s ? '#fff' : '#A1A1AA',
            border: 'none', padding: '10px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', borderLeft: active === s ? '3px solid #D90000' : '3px solid transparent',
          }}>{SECTION_META[s]?.label || s}{getP(draft, s) ? ' •' : ''}</button>
        ))}
      </nav>
      <div>
        <h4 style={{ ...sa.heading, fontSize: 22, marginTop: 0 }}>{meta.label}</h4>
        {meta.note && <p style={{ color: '#A1A1AA', fontSize: 13 }}>{meta.note}</p>}
        {active === 'branding' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, padding: 12, border: '1px dashed #27272A' }}>
            {getP(data, 'branding.logo_path') ? <img alt="logo" src={`${apiOrigin()}/api/public/branding/logo/${companyId}?v=${Date.now()}`} style={{ height: 48 }} /> : <span style={{ color: '#A1A1AA', fontSize: 13 }}>No logo uploaded</span>}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
            <SaButton variant="ghost" onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn"><Upload size={14} /> Upload logo</SaButton>
          </div>
        )}
        {active === 'permissions' ? (
          <div>
            <p style={{ color: '#A1A1AA', fontSize: 13 }}>Sections this company's admins may edit themselves. Everything else is Super Admin only.</p>
            {Object.keys(SECTION_META).filter((k) => !['permissions', 'features'].includes(k)).map((k) => {
              const list = value('permissions', 'company_admin_editable') || [];
              return (<label key={k} style={{ display: 'flex', gap: 10, padding: '8px 0', fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" data-testid={`perm-${k}`} checked={list.includes(k)} onChange={(e) => change('permissions', 'company_admin_editable', e.target.checked ? [...list, k] : list.filter((x) => x !== k))} /> {SECTION_META[k].label}
              </label>);
            })}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: meta.fields.length > 6 ? '1fr 1fr' : '1fr', gap: '0 16px' }}>{meta.fields.map(renderField)}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <SaButton data-testid="settings-save-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</SaButton>
        </div>
      </div>
    </div>
  );
};
